import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DimensionBreakdownReport from './DimensionBreakdownReport';
import RadarChartCard from './RadarChartCard';
import type { PsychologicalRadarGroup, PsychologicalScoreCardItem } from './model';
import Badge, { type BadgeColor } from '@/components/common/Badge';
import MetricCard from '@/components/common/MetricCard';
import Section from '@/components/common/Section';

export interface CategoryBreakdownRadarSectionProps {
    title: string;
    subtitle?: string;
    iconKey?: string;
    accent?: 'purple' | 'blue' | 'muted' | 'none';
    radarGroups: PsychologicalRadarGroup[];
}

const formatScoreCardValue = (card: PsychologicalScoreCardItem, fallback: string): ReactNode => {
    const display = card.value || fallback;

    if (!card.badge) {
        return display;
    }

    return (
        <div className="flex items-center justify-center gap-2">
            <span>{display}</span>
            <Badge text={card.badge} color={(card.badgeColor as BadgeColor) ?? 'gray'} />
        </div>
    );
};

const CategoryBreakdownRadarSection = ({
    title,
    subtitle,
    iconKey,
    accent = 'blue',
    radarGroups,
}: CategoryBreakdownRadarSectionProps) => {
    const { t } = useTranslation('traineeReports');

    const sections = useMemo(() => radarGroups, [radarGroups]);

    return (
        <div className="col-span-8">
            <Section title={title} subtitle={subtitle} iconKey={iconKey} accent={accent}>
                <div className="space-y-6">
                    {sections.map(s => (
                        <div key={s.key} className="space-y-5">
                            <div className="pdf-avoid-break flex w-full gap-5 overflow-x-auto pb-1">
                                {s.scoreCards.map(c => (
                                    <MetricCard
                                        key={c.label}
                                        label={c.label}
                                        value={formatScoreCardValue(c, t('na'))}
                                        variant="default"
                                        minWidth={150}
                                    />
                                ))}
                            </div>

                            <div className={`grid gap-6 ${s.charts.length > 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
                                {s.charts.map((c, idx) => (
                                    <div key={idx} className="bg-neutral-0 rounded-xl p-6">
                                        <RadarChartCard
                                            data={c.data}
                                            maxValueData={c.maxDomain ?? 10}
                                            series={c.series}
                                            showTeam={c.series ? undefined : !!c.showTeam}
                                            variant={c.variant ?? 'bare'}
                                            height={c.height ?? 280}
                                            footer={c.footer}
                                        />
                                    </div>
                                ))}
                            </div>

                            {s.dimensionBreakdown && s.dimensionBreakdown.length > 0 && (
                                <DimensionBreakdownReport
                                    title={t('dimensionBreakdown')}
                                    items={s.dimensionBreakdown}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
};

export default CategoryBreakdownRadarSection;
